import {
  AdCreative,
  AdCreativeType,
  CreativeCopyPatch,
  CreativeCopySlidePatch,
  FBCallToAction,
} from '@marketing-milk/interfaces'
import { LocalAdPreviewCreative } from 'components/ad-preview'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { DynamicCombination, DynamicPools, pinnedCombination } from './dynamicCombination'
import { DraftCard, DraftMedia, previewSingleMedia } from './mediaDraft'

/**
 * The copy an employee is editing on the review surface, before they save it.
 *
 * The draft IS the request body — `CreativeCopyPatch`, the schema the server
 * validates against — so there is no draft-to-payload mapping step that could
 * drop or rename a field on the way out. Every field is filled in from the saved
 * creative rather than left absent, because the fields are on screen: a blank
 * one means "the ad has no description", not "leave the description alone".
 */
export function copyDraftFromCreative(creative: AdCreative): CreativeCopyPatch {
  const draft: CreativeCopyPatch = {
    primaryText: creative.primaryText ?? '',
    headline: creative.linkHeadline ?? '',
    description: creative.linkDescription ?? '',
    url: creative.linkWebsiteURL ?? '',
  }

  // A CTA is a choice from a fixed list, and an ad can genuinely have none.
  // Absence is the initial no-button state; an explicit null is reserved for a
  // user clearing a CTA that was previously present.
  if (creative.callToAction) draft.callToAction = creative.callToAction

  if (creative.format === AdCreativeType.carousel) {
    draft.slides = (creative.carouselSlides ?? [])
      .filter(slide => slide.id !== undefined)
      .map(
        (slide): CreativeCopySlidePatch => ({
          id: slide.id!,
          headline: slide.linkHeadline ?? '',
          description: slide.linkDescription ?? '',
          url: slide.linkWebsiteURL ?? '',
        })
      )
  }

  if (isDynamicCreative(creative)) {
    draft.pools = {
      primaryTexts: creative.dynamicConfig.primaryTexts ?? [],
      headlines: creative.dynamicConfig.headlines ?? [],
      descriptions: creative.dynamicConfig.descriptions ?? [],
      callToActions: creative.dynamicConfig.callToActions ?? [],
    }
  }

  return draft
}

/**
 * The pools as the draft currently has them, so the picker lists what is typed
 * and the preview assembles from it. `assets` comes in already drafted — the
 * media half is staged as operations, not as pool text.
 */
export function draftPools(
  saved: DynamicPools,
  draft: CreativeCopyPatch,
  assets: DraftMedia[]
): DynamicPools {
  const pools = draft.pools
  if (!pools) return { ...saved, assets }
  return {
    assets,
    primaryTexts: pools.primaryTexts ?? [],
    headlines: pools.headlines ?? [],
    descriptions: pools.descriptions ?? [],
    callToActions: (pools.callToActions ?? []) as FBCallToAction[],
  }
}

/** The media the editor is showing, derived once and shared with the preview. */
export interface DraftMediaView {
  single?: DraftMedia
  cards: DraftCard[]
  /** Already drafted, assets included. */
  pools: DynamicPools
}

/**
 * The ad as the draft would render it, in the shape the local renderer reads —
 * which is what lets the preview redraw as someone types instead of waiting on a
 * save and then on Facebook.
 */
export function copyDraftToPreview(
  creative: AdCreative,
  draft: CreativeCopyPatch,
  combination: DynamicCombination,
  media: DraftMediaView
): LocalAdPreviewCreative {
  if (isDynamicCreative(creative)) {
    // The pinned-combination resolver already merges pools down to one rendering;
    // hand it the drafted pools rather than growing a second merge here.
    return pinnedCombination(
      { ...creative, ...draftedCreativeFields(creative, draft) },
      media.pools,
      combination
    )
  }

  const base = { ...creative, ...draftedCreativeFields(creative, draft) }

  if (creative.format === AdCreativeType.carousel) {
    return {
      ...base,
      // The cards the editor is showing, media and all — the same derivation, so
      // the preview cannot disagree with the tiles beside it.
      carouselSlides: media.cards.map(card => ({
        linkHeadline: card.headline,
        linkDescription: card.description,
        linkWebsiteURL: card.url,
        ...previewMedia(card.media),
      })),
    }
  }

  // The renderer branches on `format`, so a staged image↔video swap has to move
  // it. With no media resolved at all the ad is left as it was, rather than being
  // called an image ad because nothing was found.
  const single = media.single
  if (!single) return base
  return { ...base, singleImage: undefined, thumbnailURL: undefined, ...previewSingleMedia(single) }
}

/** What the media the editor is showing looks like to the local renderer. */
function previewMedia(media?: DraftMedia) {
  // `''` rather than absent: the renderer falls back to its placeholder on a
  // falsy URL, and `AdImage.url` is not optional.
  if (media?.mediaType === 'image') {
    return { mediaType: 'image' as const, image: { url: media.url ?? '' } }
  }
  if (media?.mediaType === 'video') {
    return {
      mediaType: 'video' as const,
      video: {
        source: media.source,
        storedSource: media.storedSource,
        thumbnail: media.thumbnail,
        animatedImage: media.animatedImage,
      },
    }
  }
  return {}
}

/** The creative-level fields a draft overrides, spread over a saved creative. */
function draftedCreativeFields(
  creative: AdCreative,
  draft: CreativeCopyPatch
): Partial<AdCreative> {
  return {
    primaryText: draft.primaryText ?? creative.primaryText,
    linkHeadline: draft.headline ?? creative.linkHeadline,
    linkDescription: draft.description ?? creative.linkDescription,
    callToAction:
      draft.callToAction === null ? undefined : draft.callToAction ?? creative.callToAction,
    linkWebsiteURL: draft.url ?? creative.linkWebsiteURL,
  }
}
